/**
 * useQuestionnaireResponses — per-user response collection and retrieval for questionnaires.
 *
 * Integrates with the data-service to persist questionnaire responses.
 * Uses dataApi for CRUD and refresh-store for cross-component sync.
 *
 * Storage pattern: each response = one record in data-service schema.
 * Fields: respondentId, respondentName, status, responses (JSON), startedAt, completedAt.
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type {
  QuestionnaireResponse,
  QuestionnaireStep,
  AggregatedResponses,
  WritebackConfig,
  StepResponse,
} from '@/types/pm-types';
import { aggregateResponses } from '@/utils/questionnaireAggregation';
import dataApi from '@/services/api/dataApi';
import { getRefetch, registerRefetch, unregisterRefetch } from '@/stores/data/refresh-store';

export interface UseQuestionnaireResponsesOptions {
  /** The questionnaire component ID */
  questionnaireId: string;
  /** Writeback config with groupId + schemaId for data-service persistence */
  writebackConfig?: WritebackConfig;
  /** Current user ID (for per-user response tracking) */
  respondentId?: string;
  /** Current user display name */
  respondentName?: string;
  /** All questionnaire steps (for aggregation) */
  steps: QuestionnaireStep[];
  /** Whether the questionnaire allows multiple submissions per user */
  allowMultipleSubmissions?: boolean;
}

export interface UseQuestionnaireResponsesResult {
  /** Current user's response (null if not started) */
  myResponse: QuestionnaireResponse | null;
  /** Save answers for a single step (auto-save on step transition) */
  saveStepResponse: (stepId: string, answers: Record<string, unknown>) => Promise<void>;
  /** Mark the response as completed (final submission) */
  submitResponse: (allAnswers: Record<string, Record<string, unknown>>) => Promise<void>;
  /** All collected responses from all respondents */
  allResponses: QuestionnaireResponse[];
  /** Aggregated response data (computed from allResponses) */
  aggregated: AggregatedResponses | null;
  /** Whether an initial data load is in progress */
  isLoading: boolean;
  /** Whether a save/submit is in progress */
  isSaving: boolean;
  /** Last error message (cleared on next successful operation) */
  error: string | null;
  /** Re-fetch all responses from the data service */
  refetch: () => Promise<void>;
  /** Whether data-service persistence is configured and available */
  isPersistenceEnabled: boolean;
}

function isWritebackEnabled(config?: WritebackConfig): config is WritebackConfig & { enabled: true } {
  return !!config?.enabled && !!config.groupId && !!config.schemaId;
}

/** Parse a raw data-service record into a QuestionnaireResponse. */
function parseResponseRecord(
  record: Record<string, unknown>,
  questionnaireId: string,
): QuestionnaireResponse {
  return {
    questionnaireId,
    respondentId: String(record['respondentId'] || record['_id'] || ''),
    respondentName: String(record['respondentName'] || 'Anonymous'),
    status: record['status'] === 'completed' ? 'completed' : 'in_progress',
    responses: (typeof record['responses'] === 'object' && record['responses'] !== null
      ? record['responses']
      : {}) as Record<string, StepResponse>,
    startedAt: String(record['startedAt'] || new Date().toISOString()),
    completedAt: record['completedAt'] ? String(record['completedAt']) : undefined,
  };
}

export function useQuestionnaireResponses({
  questionnaireId,
  writebackConfig,
  respondentId,
  respondentName,
  steps,
  allowMultipleSubmissions = false,
}: UseQuestionnaireResponsesOptions): UseQuestionnaireResponsesResult {
  const [allResponses, setAllResponses] = useState<QuestionnaireResponse[]>([]);
  const [myRecordId, setMyRecordId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const persistenceEnabled = isWritebackEnabled(writebackConfig);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Derive current user's response from allResponses
  const myResponse = useMemo(() => {
    if (!respondentId) return null;
    return allResponses.find((r) => r.respondentId === respondentId) || null;
  }, [allResponses, respondentId]);

  // Compute aggregation whenever responses change
  const aggregated = useMemo(() => {
    if (allResponses.length === 0) return null;
    return aggregateResponses(allResponses, steps);
  }, [allResponses, steps]);

  // Fetch all responses from data service
  const fetchResponses = useCallback(async () => {
    if (!persistenceEnabled) return;
    const { groupId, schemaId } = writebackConfig!;

    setIsLoading(true);
    setError(null);

    try {
      const result = await dataApi.queryRecords(groupId, schemaId, {
        limit: 500,
        sortBy: 'startedAt',
        sortOrder: 'desc',
      });

      if (!mountedRef.current) return;

      const parsed = result.records.map((r) => parseResponseRecord(r, questionnaireId));
      setAllResponses(parsed);

      // Find our existing record ID (for updates)
      if (respondentId) {
        const myRecord = result.records.find(
          (r) => String(r['respondentId']) === respondentId && r['status'] !== 'completed',
        );
        if (myRecord) {
          setMyRecordId(String(myRecord['_id'] || ''));
        }
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load responses');
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [persistenceEnabled, writebackConfig, questionnaireId, respondentId]);

  // Initial fetch + register for refresh-store
  useEffect(() => {
    if (!persistenceEnabled) {
      return undefined;
    }

    fetchResponses();
    registerRefetch(questionnaireId, fetchResponses);
    return () => unregisterRefetch(questionnaireId);
  }, [persistenceEnabled, fetchResponses, questionnaireId]);

  // Save answers for a single step
  const saveStepResponse = useCallback(async (
    stepId: string,
    answers: Record<string, unknown>,
  ) => {
    if (!persistenceEnabled || !respondentId) return;
    const { groupId, schemaId } = writebackConfig!;

    setIsSaving(true);
    setError(null);

    try {
      // Build the step response
      const stepResp: StepResponse = {
        stepId,
        answers,
        answeredAt: new Date().toISOString(),
      };

      if (myRecordId) {
        // Update existing in-progress response — merge step answers
        const existing = myResponse?.responses || {};
        await dataApi.updateRecord(groupId, schemaId, myRecordId, {
          responses: { ...existing, [stepId]: stepResp },
        });
      } else {
        // Create new response record
        const responseData = {
          respondentId,
          respondentName: respondentName || 'Anonymous',
          status: 'in_progress',
          responses: { [stepId]: stepResp },
          startedAt: new Date().toISOString(),
        };
        const result = await dataApi.createRecord(groupId, schemaId, responseData, {
          source: 'questionnaire',
          tags: ['questionnaire-response', questionnaireId],
        });
        if (mountedRef.current && result.insertedId) {
          setMyRecordId(result.insertedId);
        }
      }

      // Trigger sibling component refresh
      const refetch = getRefetch(questionnaireId);
      if (refetch) refetch().catch(() => {});
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to save step response');
    } finally {
      if (mountedRef.current) setIsSaving(false);
    }
  }, [persistenceEnabled, writebackConfig, respondentId, respondentName, myRecordId, myResponse, questionnaireId]);

  // Submit the completed response
  const submitResponse = useCallback(async (
    allAnswers: Record<string, Record<string, unknown>>,
  ) => {
    if (!persistenceEnabled || !respondentId) return;
    const { groupId, schemaId } = writebackConfig!;

    // Check if already submitted and multiple submissions not allowed
    if (!allowMultipleSubmissions && myResponse?.status === 'completed') {
      setError('You have already submitted a response');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Build complete responses object
      const responseEntries: Record<string, StepResponse> = {};
      for (const [stepId, answers] of Object.entries(allAnswers)) {
        responseEntries[stepId] = {
          stepId,
          answers,
          answeredAt: new Date().toISOString(),
        };
      }

      const completedData = {
        respondentId,
        respondentName: respondentName || 'Anonymous',
        status: 'completed',
        responses: responseEntries,
        startedAt: myResponse?.startedAt || new Date().toISOString(),
        completedAt: new Date().toISOString(),
      };

      if (myRecordId) {
        await dataApi.updateRecord(groupId, schemaId, myRecordId, completedData);
      } else {
        await dataApi.createRecord(groupId, schemaId, completedData, {
          source: 'questionnaire',
          tags: ['questionnaire-response', questionnaireId],
        });
      }

      // Re-fetch to get latest data
      await fetchResponses();
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to submit response');
    } finally {
      if (mountedRef.current) setIsSaving(false);
    }
  }, [persistenceEnabled, writebackConfig, respondentId, respondentName, myRecordId, myResponse, allowMultipleSubmissions, questionnaireId, fetchResponses]);

  return {
    myResponse,
    saveStepResponse,
    submitResponse,
    allResponses,
    aggregated,
    isLoading,
    isSaving,
    error,
    refetch: fetchResponses,
    isPersistenceEnabled: persistenceEnabled,
  };
}
